import { getAdminName } from "../constants";
import { getEncryptedAuthToken } from "../pureFunctions";

const AUTH_SESSION_KEY = "ninfa-admin-auth-session";
export const ADMIN_USER_TYPE = "admin";
export const PRINTER_USER_TYPE = "printer";
export const DEFAULT_ADMIN_PATH = "/";
export const PHYSICAL_ORDERS_PATH = "/physicalOrders";

type WalletAuthSession = {
  mode: "wallet";
  walletAddress: string;
  credential: string;
};

type EmailAuthSession = {
  mode: "email";
  email: string;
  password: string;
};

export type StoredAuthSession = WalletAuthSession | EmailAuthSession;

export type AuthRequestHeaders = {
  token: string;
  address?: string;
  email?: string;
  password?: string;
};

export type AuthenticatedUser = {
  userType: string;
  username?: string | null;
  email?: string | null;
  walletAddress?: string | null;
};

const getLocalStorage = () =>
  typeof window === "undefined" ? null : window.localStorage;

const getSessionStorage = () =>
  typeof window === "undefined" ? null : window.sessionStorage;

export const createWalletAuthSession = (
  walletAddress: string,
  signature: string,
): WalletAuthSession => ({
  mode: "wallet",
  walletAddress,
  credential: signature,
});

export const createEmailAuthSession = (
  email: string,
  password: string,
): EmailAuthSession => ({
  mode: "email",
  email,
  password,
});

export const setStoredAuthSession = (session: StoredAuthSession) => {
  if (session.mode === "wallet") {
    getLocalStorage()?.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    getLocalStorage()?.setItem("signature", session.credential);
    getSessionStorage()?.removeItem(AUTH_SESSION_KEY);
    return;
  }

  getSessionStorage()?.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
  getLocalStorage()?.removeItem(AUTH_SESSION_KEY);
};

export const getStoredAuthSession = (): StoredAuthSession | null => {
  const rawSession =
    getSessionStorage()?.getItem(AUTH_SESSION_KEY) ??
    getLocalStorage()?.getItem(AUTH_SESSION_KEY);

  if (!rawSession) {
    return null;
  }

  try {
    const parsedSession = JSON.parse(rawSession) as StoredAuthSession;
    if (parsedSession.mode === "wallet") {
      return parsedSession.walletAddress && parsedSession.credential
        ? parsedSession
        : null;
    }

    if (parsedSession.mode === "email") {
      return parsedSession.email && parsedSession.password ? parsedSession : null;
    }

    return null;
  } catch {
    return null;
  }
};

export const clearStoredAuthSession = () => {
  getLocalStorage()?.removeItem(AUTH_SESSION_KEY);
  getLocalStorage()?.removeItem("signature");
  getSessionStorage()?.removeItem(AUTH_SESSION_KEY);
};

export const getAuthHeaders = (
  session: StoredAuthSession,
): AuthRequestHeaders => {
  if (session.mode === "wallet") {
    return {
      token: getEncryptedAuthToken(session.credential),
      address: session.walletAddress,
    };
  }

  return {
    token: getEncryptedAuthToken(session.password),
    email: session.email,
    password: session.password,
  };
};

export const getStoredAuthHeaders = (): AuthRequestHeaders | null => {
  const session = getStoredAuthSession();
  return session ? getAuthHeaders(session) : null;
};

const normalizeAdminPathname = (pathname?: string | null) => {
  if (!pathname || pathname === "/") {
    return DEFAULT_ADMIN_PATH;
  }

  return pathname.replace(/\/+$/, "");
};

export const isAllowedAdminUserType = (userType?: string | null) =>
  userType === ADMIN_USER_TYPE || userType === PRINTER_USER_TYPE;

export const isPrinterUserType = (userType?: string | null) =>
  userType === PRINTER_USER_TYPE;

export const getDefaultAdminPath = (userType?: string | null) =>
  isPrinterUserType(userType) ? PHYSICAL_ORDERS_PATH : DEFAULT_ADMIN_PATH;

export const canAccessAdminPathname = (
  userType?: string | null,
  pathname?: string | null,
) => {
  if (!isAllowedAdminUserType(userType)) {
    return false;
  }

  if (!isPrinterUserType(userType)) {
    return true;
  }

  return normalizeAdminPathname(pathname) === PHYSICAL_ORDERS_PATH;
};

export const getAuthDisplayName = (
  session: StoredAuthSession | null,
  user: AuthenticatedUser | null,
) => {
  const username = user?.username?.trim();
  if (username) {
    return username;
  }

  const email =
    user?.email?.trim() || (session?.mode === "email" ? session.email : "");
  if (email) {
    return email;
  }

  const walletAddress =
    user?.walletAddress || (session?.mode === "wallet" ? session.walletAddress : "");
  if (!walletAddress) {
    return "unknown";
  }

  const adminName = getAdminName(walletAddress);
  return adminName !== "unknown" ? adminName : walletAddress;
};
